"use client";

import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";

import { cn } from "@/app/_module/lib/utils";
import { buttonVariants } from "@/app/_module/components/ui/button";
import { useDeviceDetection } from "@/app/_module/hooks/useDeviceDetection";

const AlertDialog = AlertDialogPrimitive.Root;

const AlertDialogTrigger = AlertDialogPrimitive.Trigger;

const AlertDialogPortal = AlertDialogPrimitive.Portal;

const AlertDialogOverlay = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Overlay
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
    ref={ref}
  />
));
AlertDialogOverlay.displayName = AlertDialogPrimitive.Overlay.displayName;

// Helper function to get responsive max-width class based on device type
const getAlertDialogResponsiveMaxWidth = (
  isMobile: boolean,
  isTablet: boolean,
  isLaptop: boolean,
  isDesktop: boolean,
  size: "sm" | "md" | "lg" | "xl" | "2xl" = "lg"
): string => {
  const sizeMap = {
    sm: {
      mobile: "max-w-xs",
      tablet: "max-w-sm",
      laptop: "max-w-sm",
      desktop: "max-w-md",
    },
    md: {
      mobile: "max-w-sm",
      tablet: "max-w-md",
      laptop: "max-w-md",
      desktop: "max-w-lg",
    },
    lg: {
      mobile: "max-w-sm",
      tablet: "max-w-md",
      laptop: "max-w-lg",
      desktop: "max-w-xl",
    },
    xl: {
      mobile: "max-w-md",
      tablet: "max-w-lg",
      laptop: "max-w-xl",
      desktop: "max-w-2xl",
    },
    "2xl": {
      mobile: "max-w-md",
      tablet: "max-w-xl",
      laptop: "max-w-2xl",
      desktop: "max-w-3xl",
    },
  };

  const sizes = sizeMap[size];
  if (isMobile) return sizes.mobile;
  if (isTablet) return sizes.tablet;
  if (isLaptop) return sizes.laptop;
  if (isDesktop) return sizes.desktop;
  return sizes.laptop;
};

const AlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <AlertDialogPortal>
    <AlertDialogOverlay />
    {/* shell flex-centering wrapper — WKWebView won't shrink-wrap
        inset-positioned boxes; see ui/dialog.tsx DialogContent note */}
    <div className="app-dialog-wrap">
    <AlertDialogPrimitive.Content
      ref={ref}
      className={cn(
        "app-dialog-content fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg [transform:translate(-50%,-50%)] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}
    />
    </div>
  </AlertDialogPortal>
));
AlertDialogContent.displayName = AlertDialogPrimitive.Content.displayName;

// Responsive AlertDialogContent that auto-scales based on device type
interface ResponsiveAlertDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Content> {
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
}

const ResponsiveAlertDialogContent = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Content>,
  ResponsiveAlertDialogContentProps
>(({ className, size = "lg", ...props }, ref) => {
  const { isMobile, isTablet, isLaptop, isDesktop } = useDeviceDetection();
  const responsiveMaxWidth = getAlertDialogResponsiveMaxWidth(isMobile, isTablet, isLaptop, isDesktop, size);

  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      {/* shell flex-centering wrapper — see note above */}
      <div className="app-dialog-wrap">
      <AlertDialogPrimitive.Content
        ref={ref}
        className={cn(
          "app-dialog-content fixed left-[50%] top-[50%] z-50 grid w-full [transform:translate(-50%,-50%)] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          responsiveMaxWidth,
          className
        )}
        {...props}
      />
      </div>
    </AlertDialogPortal>
  );
});
ResponsiveAlertDialogContent.displayName = "ResponsiveAlertDialogContent";

const AlertDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
);
AlertDialogHeader.displayName = "AlertDialogHeader";

const AlertDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className
    )}
    {...props}
  />
);
AlertDialogFooter.displayName = "AlertDialogFooter";

const AlertDialogTitle = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold", className)}
    {...props}
  />
));
AlertDialogTitle.displayName = AlertDialogPrimitive.Title.displayName;

const AlertDialogDescription = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
AlertDialogDescription.displayName =
  AlertDialogPrimitive.Description.displayName;

const AlertDialogAction = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Action>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Action
    ref={ref}
    className={cn(buttonVariants(), className)}
    {...props}
  />
));
AlertDialogAction.displayName = AlertDialogPrimitive.Action.displayName;

const AlertDialogCancel = React.forwardRef<
  React.ElementRef<typeof AlertDialogPrimitive.Cancel>,
  React.ComponentPropsWithoutRef<typeof AlertDialogPrimitive.Cancel>
>(({ className, ...props }, ref) => (
  <AlertDialogPrimitive.Cancel
    ref={ref}
    className={cn(
      buttonVariants({ variant: "outline" }),
      "mt-2 sm:mt-0",
      className
    )}
    {...props}
  />
));
AlertDialogCancel.displayName = AlertDialogPrimitive.Cancel.displayName;

export {
  AlertDialog,
  AlertDialogPortal,
  AlertDialogOverlay,
  AlertDialogTrigger,
  AlertDialogContent,
  ResponsiveAlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
};
